import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPriceTypeLabel, localStorageKeys, permissionLevelEnum, systemConfigKeyEnum } from '../../../constants';
import { useToolsQuery } from '../../../hooks/queries/useToolsQuery';
import { useAuth } from '../../../hooks/useAuth';
import { useSystemConfig } from '../../../hooks/useSystemConfig';
import { Tool } from '../../../services/apiSLTP/materials/tools';
import { capitalizeFirstLetter } from '../../../utils/capitalize';
import { formatNumber } from '../../../utils/numbers';
import { InheritedPriceDisplay } from '../../domain/InheritedPriceDisplay';
import GenericAgGridTable from '../GenericAgGridTable';

interface ToolsPLITableProps {}

const ToolsPLITable: React.FC<ToolsPLITableProps> = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data: dataTools, isLoading: isToolsLoading } = useToolsQuery({ queries: { active: true } });

    const sortedTools = () => {
        return (
            dataTools?.sort((a, b) => {
                const typeA = a.toolTypeId?.displayOrder || 0;
                const typeB = b.toolTypeId?.displayOrder || 0;
                if (typeA < typeB) return -1;
                if (typeA > typeB) return 1;
                const designationA = a?.designation;
                const designationB = b?.designation;
                if (designationA < designationB) return -1;
                if (designationA > designationB) return 1;
                return 0;
            }) || []
        );
    };
    const columnDefs: ColDef<Tool>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            valueGetter: (params) => params.data?.price ?? params.data?.toolTypeId?.price ?? null,
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            cellRenderer: (params: ICellRendererParams<Tool>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data?.toolTypeId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                ) : (
                    params.value
                ),
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => (params.data ? t(getPriceTypeLabel(params.data?.priceType ?? params.data?.toolTypeId?.priceType)) : ''),
            cellRenderer: (params: ICellRendererParams<Tool>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data?.priceType}
                        inheritedValue={params.data?.toolTypeId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeLabel(value)))}
                    />
                ) : (
                    params.value
                ),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'toolTypeId.designation',
            colId: 'type',
        },
        ...(user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? ([
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      minWidth: 250,
                      colId: '_id',
                  },
              ] as ColDef<Tool>[])
            : []),
    ];

    return (
        <GenericAgGridTable
            rowData={sortedTools()}
            columnDefs={columnDefs}
            loading={isToolsLoading}
            exportCsvFileName="sales-pli-tools"
            storageKey={localStorageKeys.TABLE.PLI.TOOLS}
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default ToolsPLITable;
